import ReactGA from "react-ga4";

const TRACKING_ID = process.env.REACT_APP_GA_ID;
let isInitialized = false;

/**
 * Initialize Google Analytics 4
 * Safely initializes ReactGA once with tracking ID.
 */
export const initGA = () => {
  if (TRACKING_ID && !isInitialized) {
    try {
      ReactGA.initialize(TRACKING_ID, {
        gaOptions: {
          siteSpeedSampleRate: 100,
        },
      });
      isInitialized = true;
    } catch (err) {
      console.warn("GA4 Initialization Warning:", err);
    }
  }
};

/**
 * Track Page Views (SPA compliant)
 */
export const trackPageView = (path = window.location.pathname, title = document.title) => {
  if (isInitialized) {
    try {
      ReactGA.send({
        hitType: "pageview",
        page: path,
        title: title,
      });
    } catch (err) {
      // safe fallback
    }
  }
};

/**
 * Generic Event Dispatcher (GA4 Recommended Standard)
 */
export const trackEvent = (eventName, params = {}) => {
  if (isInitialized) {
    try {
      ReactGA.event(eventName, params);
    } catch (err) {
      // safe fallback
    }
  }
};

/**
 * GA4 Standard: file_download
 * Triggered when CV/resume is downloaded
 */
export const trackCvDownload = (fileName = "Sezgin-Akgül.pdf") => {
  trackEvent("file_download", {
    file_name: fileName,
    file_extension: "pdf",
    link_text: "Download CV",
    timestamp: new Date().toISOString(),
  });
};

/**
 * GA4 Standard: generate_lead
 * Triggered on contact form interactions
 */
export const trackLeadGeneration = ({ status = "success", subject = "", errorReason = "" }) => {
  trackEvent("generate_lead", {
    lead_type: "contact_form",
    status,
    subject: subject || "No Subject",
    error_reason: errorReason || undefined,
  });
};

/**
 * GA4 Standard: select_content
 * Triggered on outbound social link clicks
 */
export const trackSocialClick = (platform, destinationUrl) => {
  trackEvent("select_content", {
    content_type: "social_link",
    item_id: platform.toLowerCase(),
    item_name: platform,
    destination_url: destinationUrl,
  });
};

/**
 * GA4 Standard: select_item
 * Triggered on visiting a project showcase link
 */
export const trackProjectClick = (projectTitle, projectUrl, category = "") => {
  trackEvent("select_item", {
    item_list_name: "Projects Portfolio",
    item_id: projectTitle.toLowerCase().replace(/\s+/g, "_"),
    item_name: projectTitle,
    item_category: category,
    destination_url: projectUrl,
  });
};

/**
 * Navigation / Section Anchor Tracking
 */
export const trackNavClick = (sectionName, source = "navbar") => {
  trackEvent("navigation_click", {
    section_name: sectionName,
    navigation_source: source,
  });
};

/**
 * Section View Tracking (via Scroll / IntersectionObserver)
 */
const seenSections = new Set();
export const trackSectionView = (sectionName) => {
  if (!seenSections.has(sectionName)) {
    seenSections.add(sectionName);
    trackEvent("section_view", {
      section_name: sectionName,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * UI Preference: Theme Change
 */
export const trackThemeChange = (isDarkMode) => {
  trackEvent("theme_change", {
    preference: isDarkMode ? "dark" : "light",
  });
};

/**
 * Direct Contact Method (mailto / tel)
 */
export const trackDirectContact = (method, value) => {
  trackEvent("direct_contact_click", {
    contact_method: method, // 'email' | 'phone' | 'whatsapp'
    contact_value: value,
  });
};
